import React, { useState, useEffect, useRef, useCallback } from 'react';
import { adminApi } from '../services/api';
import '../assets/WhatsAppInboxBell.css';

/**
 * Admin-only WhatsApp chat.
 * Header icon with an unread badge. Clicking it opens a WhatsApp-style two-pane
 * window: a conversation list (grouped by parent) on the left and the selected
 * thread on the right, with a reply box. Replies are limited to WhatsApp's 24h
 * customer-service window. Admin-only; polls unread count every 30s.
 */
const WhatsAppInboxBell = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  // Conversation list (sidebar)
  const [conversations, setConversations] = useState([]);
  const [convosLoading, setConvosLoading] = useState(false);
  const [search, setSearch] = useState('');

  // Active thread (right pane)
  const [activePhone, setActivePhone] = useState(null);
  const [thread, setThread] = useState(null); // { messages, windowOpen, contactName, ... }
  const [threadLoading, setThreadLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [chatError, setChatError] = useState('');
  const chatEndRef = useRef(null);
  const overlayRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await adminApi.getWhatsappUnreadCount();
      setUnreadCount(res.data?.data?.count || 0);
    } catch (err) {
      // Silent — non-admins get 404 (adminAuth)
    }
  }, []);

  const fetchConversations = useCallback(async () => {
    try {
      setConvosLoading(true);
      const res = await adminApi.getWhatsappConversations();
      setConversations(res.data?.data || []);
    } catch (err) {
      console.error('Error fetching conversations:', err);
    } finally {
      setConvosLoading(false);
    }
  }, []);

  // Poll unread count on mount + every 30s
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Load conversations when the window opens
  useEffect(() => {
    if (isOpen) fetchConversations();
  }, [isOpen, fetchConversations]);

  // Esc closes the chat window
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') closeWindow(); };
    if (isOpen) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen]);

  // ── iOS keyboard fix ───────────────────────────────────────────────────
  // On iOS Safari, opening the keyboard does NOT shrink the layout viewport —
  // Safari scrolls the page up to reveal the focused input, which drags our
  // position:fixed overlay off-screen (it "flies away"). window.visualViewport
  // DOES track the keyboard, so while the popup is open we pin the overlay to
  // the visual viewport's live height and top offset via CSS variables. The
  // mobile CSS consumes these so the popup stays glued above the keyboard.
  useEffect(() => {
    if (!isOpen) return;
    const vv = window.visualViewport;
    if (!vv) return; // non-Safari / older browsers fall back to dvh in CSS

    const apply = () => {
      const el = overlayRef.current;
      if (!el) return;
      el.style.setProperty('--wa-vvh', `${vv.height}px`);
      el.style.setProperty('--wa-vv-top', `${vv.offsetTop}px`);
      el.style.setProperty('--wa-vv-left', `${vv.offsetLeft}px`);
      el.style.setProperty('--wa-vvw', `${vv.width}px`);
    };
    apply();
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
    };
  }, [isOpen]);

  // ── Browser back-button handling (mobile) ──────────────────────────────
  // The chat is a popup, not a route. Without this, tapping the browser's
  // back button navigates the app's own history and can bounce the user to the
  // login page. We push a synthetic history entry per level so "back" steps
  // the popup DOWN one level (thread → list → closed) instead of navigating.
  //
  // Single source of truth: popstate performs the actual UI step-down. UI
  // dismissals (×, back arrow, Esc, overlay click) just call history.back(),
  // which fires popstate — so there's never a double-pop.
  // When a UI dismissal unwinds multiple history entries at once (closeWindow
  // uses go(-2)), we must swallow the extra popstate events so they don't also
  // try to mutate state. This counts how many popstate events to ignore.
  const ignorePopsRef = useRef(0);

  useEffect(() => {
    const onPop = () => {
      if (ignorePopsRef.current > 0) {
        ignorePopsRef.current -= 1;
        return;
      }
      // A synthetic entry was popped by the user's browser Back. Step the UI
      // down one level: thread → list, or list → closed.
      setActivePhone((curr) => {
        if (curr) {
          setThread(null);
          setReplyText('');
          setChatError('');
          return null;
        }
        setIsOpen(false);
        setThread(null);
        setReplyText('');
        setChatError('');
        return null;
      });
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const loadThread = useCallback(async (phone) => {
    setThreadLoading(true);
    setChatError('');
    try {
      const res = await adminApi.getWhatsappThread(phone);
      setThread(res.data?.data || null);
      // Opening marks inbound as read server-side; refresh counts + sidebar.
      fetchUnreadCount();
      setConversations(prev => prev.map(c =>
        (c.phone.replace(/\D/g, '').slice(-8) === phone.replace(/\D/g, '').slice(-8))
          ? { ...c, unread: 0 } : c
      ));
    } catch (err) {
      setChatError(err.response?.data?.message || 'Failed to load conversation.');
    } finally {
      setThreadLoading(false);
    }
  }, [fetchUnreadCount]);

  // Open the popup (list level). Pushes a history entry so the browser back
  // button closes the popup instead of navigating the page.
  const openWindow = () => {
    window.history.pushState({ waChat: 'list' }, '');
    setIsOpen(true);
  };

  // Open a conversation (thread level). Pushes a second history entry so back
  // returns to the list before closing the popup.
  const openConversation = (phone) => {
    window.history.pushState({ waChat: 'thread' }, '');
    setActivePhone(phone);
    setThread(null);
    setReplyText('');
    setChatError('');
    loadThread(phone);
  };

  // Fully close the popup (×, Esc, overlay click). Reset state directly, then
  // unwind our synthetic history entries. Each unwound entry fires a popstate
  // that we tell the listener to ignore (we've already reset state here).
  const closeWindow = () => {
    const entries = activePhone ? 2 : 1; // thread+list, or just list
    setIsOpen(false);
    setActivePhone(null);
    setThread(null);
    setReplyText('');
    setChatError('');
    ignorePopsRef.current += entries;
    window.history.go(-entries);
  };

  // Thread → list (back arrow). Let popstate perform the single step-down.
  const backToList = () => {
    window.history.back();
  };

  const sendReply = async () => {
    const text = replyText.trim();
    if (!text || !activePhone) return;
    setSending(true);
    setChatError('');
    try {
      const res = await adminApi.sendWhatsappReply(activePhone, text);
      const sent = res.data?.data;
      setThread(prev => prev ? { ...prev, messages: [...prev.messages, sent] } : prev);
      setReplyText('');
      // Reflect the new last-message in the sidebar
      setConversations(prev => prev.map(c =>
        (c.phone.replace(/\D/g, '').slice(-8) === activePhone.replace(/\D/g, '').slice(-8))
          ? { ...c, lastText: text, lastDirection: 'out', lastAt: new Date().toISOString() } : c
      ));
    } catch (err) {
      setChatError(err.response?.data?.message || 'Failed to send message.');
    } finally {
      setSending(false);
    }
  };

  // Auto-scroll to newest message
  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [thread?.messages?.length]);

  const fmtRelative = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const mins = Math.floor((Date.now() - d.getTime()) / 60000);
    if (mins < 1) return 'now';
    if (mins < 60) return `${mins}m`;
    if (mins < 1440) return `${Math.floor(mins / 60)}h`;
    return d.toLocaleDateString('en-SG', { timeZone: 'Asia/Singapore', day: '2-digit', month: 'short' });
  };

  const fmtBubbleTime = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleString('en-SG', {
      timeZone: 'Asia/Singapore', day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit', hour12: true
    });
  };

  // Group thread messages by calendar day for date separators
  const groupByDay = (msgs) => {
    const groups = [];
    let currentKey = null;
    for (const m of msgs) {
      const d = new Date(m.createdAt);
      const key = d.toLocaleDateString('en-SG', { timeZone: 'Asia/Singapore', year: 'numeric', month: 'short', day: '2-digit' });
      if (key !== currentKey) {
        groups.push({ day: key, items: [] });
        currentKey = key;
      }
      groups[groups.length - 1].items.push(m);
    }
    return groups;
  };

  // Render WhatsApp-style inline formatting (*bold*, _italic_, ~strike~) as real
  // elements. We build React text/elements directly (never dangerouslySetHTML),
  // so this is XSS-safe regardless of message content. Unmatched markers render
  // literally. Operates line-by-line so newlines are preserved via CSS pre-wrap.
  const renderWhatsAppText = (text) => {
    if (!text) return text;
    // token: *bold*, _italic_, ~strike~ (non-greedy, no nested markers)
    const re = /(\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~)/g;
    const out = [];
    let last = 0;
    let match;
    let i = 0;
    while ((match = re.exec(text)) !== null) {
      if (match.index > last) out.push(text.slice(last, match.index));
      const tok = match[0];
      const inner = tok.slice(1, -1);
      if (tok[0] === '*') out.push(<strong key={i++}>{inner}</strong>);
      else if (tok[0] === '_') out.push(<em key={i++}>{inner}</em>);
      else out.push(<s key={i++}>{inner}</s>);
      last = match.index + tok.length;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
  };

  const filteredConvos = conversations.filter(c => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (c.contactName || '').toLowerCase().includes(q)
      || (c.tuteeNames || []).some(n => (n || '').toLowerCase().includes(q))
      || (c.phone || '').includes(q);
  });

  // Join child names Oxford-style: 1 → "A"; 2 → "A & B"; 3+ → "A, B & C".
  const joinChildren = (names) => {
    if (names.length <= 1) return names[0] || '';
    return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
  };

  // Display label:
  //  - Payment IC thread → "LIN QICHUN (Payment IC)"
  //  - Parent thread     → "Mr Eugene Ho (Elijah's parent)" or, with multiple
  //    children, "Mrs Tan (Anuttara & Aravind's parent)" (degrade gracefully)
  const makeDisplayName = (c) => {
    if (!c) return 'Parent';
    if (c.isPaymentIC) {
      return c.paymentICName ? `${c.paymentICName} (Payment IC)` : 'Payment IC';
    }
    const parentName = c.parentName;
    // Prefer the multi-child tuteeNames array; fall back to the single
    // contactName so legacy/single-child responses look identical to before.
    const childrenLabel = (Array.isArray(c.tuteeNames) && c.tuteeNames.length)
      ? joinChildren(c.tuteeNames)
      : c.contactName;
    if (parentName && childrenLabel) return `${parentName} (${childrenLabel}'s parent)`;
    if (parentName) return parentName;
    if (childrenLabel) return `${childrenLabel}'s parent`;
    return 'Parent';
  };

  const activeConvoName = makeDisplayName(thread);

  return (
    <div className="wa-inbox-container">
      <button
        className="wa-inbox-button"
        onClick={openWindow}
        aria-label={`WhatsApp chat${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        title="WhatsApp chat with parents"
      >
        <span className="wa-icon" aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2zm0 18.15h-.01c-1.52 0-3.01-.41-4.3-1.18l-.31-.18-3.19.84.85-3.11-.2-.32a8.2 8.2 0 01-1.26-4.36c0-4.54 3.7-8.24 8.24-8.24 2.2 0 4.27.86 5.82 2.42a8.18 8.18 0 012.41 5.83c0 4.54-3.69 8.23-8.24 8.23z"/>
          </svg>
        </span>
        {unreadCount > 0 && (
          <span className="wa-inbox-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className="wa-chat-overlay" ref={overlayRef} onClick={closeWindow}>
          <div className={`wa-chat-window ${activePhone ? 'has-active' : ''}`} onClick={(e) => e.stopPropagation()}>

            {/* Sidebar: conversation list */}
            <aside className="wa-chat-sidebar">
              <div className="wa-chat-sidebar-head">
                <h3>Chats</h3>
                <button className="wa-chat-close" onClick={closeWindow} aria-label="Close">×</button>
              </div>
              <div className="wa-chat-search">
                <input
                  type="text"
                  placeholder="Search name or number…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="wa-convo-list">
                {convosLoading ? (
                  <div className="wa-chat-empty">Loading…</div>
                ) : filteredConvos.length === 0 ? (
                  <div className="wa-chat-empty">No conversations yet.</div>
                ) : (
                  filteredConvos.map(c => {
                    const sameActive = activePhone &&
                      c.phone.replace(/\D/g, '').slice(-8) === activePhone.replace(/\D/g, '').slice(-8);
                    return (
                      <button
                        key={c.phone}
                        className={`wa-convo-item ${sameActive ? 'active' : ''} ${c.unread > 0 ? 'unread' : ''}`}
                        onClick={() => openConversation(c.phone)}
                      >
                        <div className="wa-convo-avatar">
                          {(c.paymentICName || c.parentName || c.contactName || 'P').charAt(0).toUpperCase()}
                        </div>
                        <div className="wa-convo-main">
                          <div className="wa-convo-top">
                            <span className="wa-convo-name">
                              {(c.isPaymentIC || c.parentName || c.contactName) ? makeDisplayName(c) : c.phone}
                            </span>
                            <span className="wa-convo-time">{fmtRelative(c.lastAt)}</span>
                          </div>
                          <div className="wa-convo-preview">
                            {c.lastDirection === 'out' ? 'You: ' : ''}{c.lastText}
                          </div>
                        </div>
                        {c.unread > 0 && <span className="wa-convo-unread">{c.unread}</span>}
                      </button>
                    );
                  })
                )}
              </div>
            </aside>

            {/* Thread pane */}
            <section className="wa-chat-main">
              {!activePhone ? (
                <div className="wa-chat-placeholder">
                  <div className="wa-chat-placeholder-icon">💬</div>
                  <p>Select a conversation to view the chat</p>
                </div>
              ) : (
                <>
                  <div className="wa-chat-header">
                    <button
                      className="wa-chat-back"
                      onClick={backToList}
                      aria-label="Back to conversations"
                    >‹</button>
                    <div className="wa-chat-title">
                      <span className="wa-chat-name">{activeConvoName}</span>
                      <span className="wa-chat-phone">{activePhone}</span>
                    </div>
                  </div>

                  <div className="wa-chat-body">
                    {threadLoading ? (
                      <div className="wa-chat-empty">Loading conversation…</div>
                    ) : !thread || thread.messages.length === 0 ? (
                      <div className="wa-chat-empty">No messages yet.</div>
                    ) : (
                      groupByDay(thread.messages).map((g, gi) => (
                        <div key={gi} className="wa-day-group">
                          <div className="wa-day-sep"><span>{g.day}</span></div>
                          {g.items.map(m => (
                            <div
                              key={m._id}
                              className={`wa-bubble ${m.direction === 'out' ? 'wa-bubble-out' : 'wa-bubble-in'}`}
                            >
                              <div className="wa-bubble-text">{m.text ? renderWhatsAppText(m.text) : `(${m.type})`}</div>
                              <div className="wa-bubble-time">{fmtBubbleTime(m.createdAt)}</div>
                            </div>
                          ))}
                        </div>
                      ))
                    )}
                    <div ref={chatEndRef} />
                  </div>

                  {chatError && <div className="wa-chat-error">{chatError}</div>}

                  <div className="wa-chat-footer">
                    {thread && !thread.windowOpen ? (
                      <div className="wa-chat-closed-note">
                        You can only send a free message within 24 hours of their last message.
                        Ask them to reply or tap a button first.
                      </div>
                    ) : (
                      <div className="wa-chat-input-row">
                        <textarea
                          className="wa-chat-input"
                          placeholder="Type a message…"
                          rows={1}
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); }
                          }}
                          disabled={sending || threadLoading}
                        />
                        <button
                          className="wa-chat-send"
                          onClick={sendReply}
                          disabled={sending || threadLoading || !replyText.trim()}
                        >
                          {sending ? '…' : 'Send'}
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </section>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppInboxBell;
